import {
  Box,
  Fab,
  Tooltip,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AutoAwesome, ChevronLeft } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

interface AiFabProps {
  openAI: boolean;
  setOpenAI: (next: boolean) => void;
  /**
   * Label shown next to the button on hover. Defaults to the Italian
   * "Assistente AI Flowenti" — pages with a translated drawer can pass
   * their own copy if they want to localize this string.
   */
  label?: string;
}

const FAB_STORAGE_KEY = "flowenti.aiFab.expanded";

/**
 * Continuous soft glow that hints at interactivity even when the user
 * is not hovering. Slow enough to not be annoying (2.4s) and gentle in
 * amplitude so it reads as "alive" rather than "alert".
 */
const pulseGlow = keyframes`
  0%   { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 0 rgba(84, 189, 193, 0.45); }
  50%  { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 14px rgba(84, 189, 193, 0); }
  100% { box-shadow: 0 6px 18px rgba(84, 189, 193, 0.35), 0 0 0 0 rgba(84, 189, 193, 0); }
`;

const labelSlideIn = keyframes`
  from { transform: translateX(12px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

/**
 * Floating Action Button that opens the AI assistant drawer.
 *
 * Positioned in the bottom-right corner, just above the version chip.
 * The button hides itself when the drawer is already open so the user
 * is not offered a redundant "open" affordance.
 *
 * Mobile behaviour (≤ 900px wide):
 *  - The FAB defaults to a collapsed state and shows only a small
 *    chevron `<` anchored to the right edge of the viewport. This
 *    keeps the chat surface area uncluttered — the FAB is still
 *    discoverable but doesn't steal screen real estate from the rest
 *    of the mobile UI.
 *  - Tapping the chevron expands it into the full FAB (with the
 *    pulsing glow and the icon). Tapping the FAB itself opens the
 *    drawer; long-pressing (or tapping again) collapses it back to
 *    the chevron.
 *  - The expanded/collapsed state is persisted in localStorage so the
 *    user's preference survives page navigation.
 *
 * Visual behaviour (desktop and expanded mobile):
 *  - Always-on pulsing glow to suggest interactivity.
 *  - On hover: the glow intensifies, the button scales up slightly,
 *    and a label slides in from the right naming the assistant.
 */
const AiFab: React.FC<AiFabProps> = ({
  openAI,
  setOpenAI,
  label = "Assistente AI Flowenti",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Track hover locally so we can render the label without keeping
  // a persistent node in the DOM when not hovered.
  const [hovered, setHovered] = useState(false);

  // Mobile collapsed/expanded state. Initial value comes from
  // localStorage so the user does not have to re-toggle every visit.
  const [mobileExpanded, setMobileExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(FAB_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(FAB_STORAGE_KEY, String(mobileExpanded));
    } catch {
      // localStorage might be unavailable in private browsing — fail silently.
    }
  }, [mobileExpanded]);

  if (openAI) {
    // Hiding cleanly is preferable to "pointer-events: none" because
    // the drawer itself occupies the right side of the viewport and
    // we don't want the FAB to bleed through with a tooltip on focus.
    return null;
  }

  // Mobile collapsed: just the chevron, anchored to the right edge.
  if (isMobile && !mobileExpanded) {
    return (
      <Tooltip title={label} placement="left" arrow>
        <Box
          id="ai-fab-reveal"
          role="button"
          aria-label={label}
          onClick={() => setMobileExpanded(true)}
          sx={{
            position: "fixed",
            // 56px leaves room for the iOS home indicator (env()).
            // The fallback of 56px keeps Android/desktop at the original
            // distance; iOS pushes the chevron up by the safe-area inset.
            bottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
            right: 0,
            zIndex: 9999999999999,
            width: 28,
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "linear-gradient(135deg, #54bdc1 0%, #1e355f 100%)",
            color: "common.white",
            borderTopLeftRadius: 14,
            borderBottomLeftRadius: 14,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.18)",
            transition: "transform 200ms ease, box-shadow 200ms ease",
            "&:hover": {
              transform: "translateX(-2px)",
              boxShadow: "0 6px 18px rgba(84, 189, 193, 0.45)",
            },
            "&:active": { transform: "translateX(0) scale(0.98)" },
            "& .MuiSvgIcon-root": { fontSize: 22 },
          }}
        >
          <ChevronLeft />
        </Box>
      </Tooltip>
    );
  }

  const handlePrimaryClick = () => {
    if (isMobile) {
      // On mobile, the first tap opens the drawer; a second tap on the
      // (now collapsed) chevron while the drawer is closed re-expands it.
      setOpenAI(true);
    } else {
      setOpenAI(true);
    }
  };

  const handleCollapse = (e: React.MouseEvent) => {
    // When the user taps the FAB on mobile, we open the drawer. To let
    // them collapse the FAB without opening the drawer, we attach a
    // long-press style: a small dot in the top-right corner that is
    // only visible when expanded, and tapping it collapses the FAB.
    e.stopPropagation();
    setMobileExpanded(false);
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "fixed",
        // 36px leaves breathing room above the version chip at bottom: 8px
        // (and the home indicator on iOS via env()).
        bottom: "calc(36px + env(safe-area-inset-bottom, 0px))",
        right: 16,
        zIndex: 9999999999999,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {hovered && (
        <Box
          role="tooltip"
          aria-hidden
          sx={{
            backgroundColor: "background.paper",
            color: "primary.main",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 2,
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
            px: 1.5,
            py: 0.75,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            animation: `${labelSlideIn} 200ms ease-out`,
            pointerEvents: "none",
          }}
        >
          {label}
        </Box>
      )}
      <Tooltip title={label} placement="left" arrow>
        <Fab
          id="ai-fab-primary"
          color="primary"
          aria-label={label}
          onClick={handlePrimaryClick}
          sx={{
            width: 56,
            height: 56,
            background: "linear-gradient(135deg, #54bdc1 0%, #1e355f 100%)",
            color: "common.white",
            boxShadow: "0 6px 18px rgba(84, 189, 193, 0.35)",
            animation: `${pulseGlow} 2400ms ease-in-out infinite`,
            transition: "transform 200ms ease, box-shadow 200ms ease",
            "&:hover": {
              transform: "scale(1.08)",
              boxShadow:
                "0 10px 24px rgba(84, 189, 193, 0.5), 0 0 0 8px rgba(84, 189, 193, 0.15)",
              animation: `${pulseGlow} 1600ms ease-in-out infinite`,
              background: "linear-gradient(135deg, #54bdc1 0%, #1e355f 100%)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 28,
            },
          }}
        >
          <AutoAwesome />
        </Fab>
      </Tooltip>

      {/* Mobile-only collapse affordance: a small dot in the top-right
          corner of the FAB. Tapping it returns the FAB to its
          chevron-only collapsed state without opening the drawer. */}
      {isMobile && (
        <Tooltip title="Nascondi" arrow placement="left">
          <Box
            id="ai-fab-collapse"
            role="button"
            aria-label="Nascondi pulsante AI"
            onClick={handleCollapse}
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "text.secondary",
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.18)",
              "&:hover": { color: "error.main" },
            }}
          >
            ×
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default AiFab;
